import {Routes} from '@angular/router';
import {notLoggedInGuard} from './core/guards/not-logged-in.guard';
import {loggedInGuard} from './core/guards/logged-in.guard';
import {ChallengeGuard} from './core/guards/temporary-token.guard';
import {UserRole} from 'patja-api';
import {rolesGuard} from './core/guards/roles.guard';
import {isUsingAccountingMaterialGuard} from './core/guards/is-using-accounting-material.guard';
import {isUsingReceiptsGuard} from './core/guards/is-using-receipts.guard';
import {uiFeatureChildGuard, uiFeatureGuard} from './core/guards/ui-feature.guard';
import {CompanyUserFeature} from './core/utils/company-user-ui-features';

export enum RoutePathNames {
  Auth = 'auth',
  Login = 'login',
  Register = 'register',
  ResetPassword = 'reset-password',
  Verify = 'verify',
  Verifying = 'verify-account',
  Home = 'home',
  Reports = 'reports',
  Receipts = 'receipts',
  StatementAndMaterial = 'statement-and-material',
  AccountingMaterial = 'accounting-material',
  Settings = 'settings',
  SetNewPassword = 'new',
  ResetPasswordLinkExpired = 'link-expired',
  CheckKey = 'check',
  SessionExpired = 'expired',
  TwoFactor = 'two-factor',
  EnableTwoFactor = 'enable-two-factor',
  ChooseMethod = 'choose-method',
  GoogleAuthenticator = 'google-auth',
  VerifyGoogleAuthenticator = 'verify-google-auth',
  VerifyBackupCode = 'verify-backup-code',
  TwoFactorOptions = 'two-factor-options',
  BackupCodes = 'backup-codes',
  Verified = 'verified-account',
  ConfirmEmailChange = 'confirm-email-change',
  TermsOfUse = 'terms-of-use',
  PrivacyPolicy = 'privacy-policy',
  SetPassword = 'set-password',
  SalesInvoices = 'sales-invoices',
  PurchaseInvoices = 'purchase-invoices',
  Chats = 'chats',
  ReceiptSelectionResult = 'receipt-selection-result',
}

export enum RoutePaths {
  Login = `${RoutePathNames.Auth}/${RoutePathNames.Login}`,
  Register = `${RoutePathNames.Auth}/${RoutePathNames.Register}`,
  ResetPassword = `${RoutePathNames.Auth}/${RoutePathNames.ResetPassword}`,
  Verify = `${RoutePathNames.Verify}`,
  Home = `${RoutePathNames.Home}`,
  Reports = `${RoutePathNames.Reports}`,
  Receipts = `${RoutePathNames.Receipts}`,
  StatementAndMaterial = `${RoutePathNames.StatementAndMaterial}`,
  AccountingMaterial = `${RoutePathNames.AccountingMaterial}`,
  Settings = `${RoutePathNames.Settings}`,
  SalesInvoices = `${RoutePathNames.SalesInvoices}`,
  PurchaseInvoices = `${RoutePathNames.PurchaseInvoices}`,
  Chats = `${RoutePathNames.Chats}`,
  SessionExpired = `${RoutePathNames.Auth}/${RoutePathNames.SessionExpired}`,
  EnableTwoFactor = `${RoutePathNames.TwoFactor}/${RoutePathNames.EnableTwoFactor}`,
  ChooseMethod = `${RoutePathNames.TwoFactor}/${RoutePathNames.ChooseMethod}`,
  GoogleAuthenticator = `${RoutePathNames.TwoFactor}/${RoutePathNames.GoogleAuthenticator}`,
  VerifyGoogleAuthenticator = `${RoutePathNames.TwoFactor}/${RoutePathNames.VerifyGoogleAuthenticator}`,
  VerifyBackupCode = `${RoutePathNames.TwoFactor}/${RoutePathNames.VerifyBackupCode}`,
  TwoFactorOptions = `${RoutePathNames.TwoFactor}/${RoutePathNames.TwoFactorOptions}`,
  TwoFactorBackupCodes = `${RoutePathNames.TwoFactor}/${RoutePathNames.BackupCodes}`,
  Verified = `${RoutePathNames.Verified}`,
  ReceiptSelectionResult = `${RoutePathNames.ReceiptSelectionResult}`,
}

export const routes: Routes = [
  {
    path: RoutePathNames.Auth,
    loadComponent: () => import('./components/auth/auth.component').then(c => c.AuthComponent),
    canActivate: [notLoggedInGuard],
    children: [
      {
        path: '',
        redirectTo: RoutePathNames.Login,
        pathMatch: 'full',
      },
      {
        path: RoutePathNames.Login,
        loadComponent: () =>
          import('./components/auth/login/login.component').then(c => c.LoginComponent),
      },
      {
        path: RoutePathNames.SessionExpired,
        loadComponent: () =>
          import('./components/auth/session-expired/session-expired.component').then(
            c => c.SessionExpiredComponent,
          ),
      },
      {
        path: RoutePathNames.ResetPassword,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./components/auth/reset-password/reset-password.component').then(
                c => c.ResetPasswordComponent,
              ),
          },
          {
            path: RoutePathNames.Verify,
            loadComponent: () =>
              import('./components/auth/verify-account/verify-account.component').then(
                c => c.VerifyAccountComponent,
              ),
            data: {parent: RoutePathNames.ResetPassword},
          },
          {
            path: RoutePathNames.SetNewPassword,
            loadComponent: () =>
              import(
                './components/auth/reset-password/set-new-password/set-new-password.component'
              ).then(c => c.SetNewPasswordComponent),
          },
          {
            path: RoutePathNames.ResetPasswordLinkExpired,
            loadComponent: () =>
              import('./components/auth/reset-password/link-expired/link-expired.component').then(
                c => c.LinkExpiredComponent,
              ),
          },
          {
            path: RoutePathNames.CheckKey,
            loadComponent: () =>
              import('./components/auth/reset-password/check-key/check-key.component').then(
                c => c.CheckKeyComponent,
              ),
          },
        ],
      },
      {
        path: RoutePathNames.SetPassword,
        loadComponent: () =>
          import('./components/auth/set-password/set-password.component').then(
            c => c.SetPasswordComponent,
          ),
      },
    ],
  },
  {
    path: RoutePathNames.Verifying,
    loadComponent: () =>
      import('./components/auth/verifying-account/verifying-account.component').then(
        c => c.VerifyingAccountComponent,
      ),
    canActivate: [notLoggedInGuard],
  },
  {
    path: RoutePathNames.Verified,
    loadComponent: () =>
      import('./components/auth/verified-account/verified-account.component').then(
        c => c.VerifiedAccountComponent,
      ),
  },
  {
    path: RoutePathNames.ConfirmEmailChange,
    loadComponent: () =>
      import('./components/auth/confirm-email-change/confirm-email-change.component').then(
        c => c.ConfirmEmailChangeComponent,
      ),
  },
  {
    path: '',
    loadComponent: () =>
      import('./components/layout/layout.component').then(c => c.LayoutComponent),
    canActivate: [loggedInGuard],
    children: [
      {
        path: '',
        redirectTo: RoutePathNames.Home,
        pathMatch: 'full',
      },
      {
        path: RoutePathNames.Home,
        loadComponent: () =>
          import('./components/layout/pages/home/home.component').then(c => c.HomeComponent),
      },
      {
        path: RoutePathNames.Reports,
        canActivate: [uiFeatureGuard],
        canActivateChild: [uiFeatureChildGuard],
        data: {requiredFeature: CompanyUserFeature.REPORTS},
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./components/layout/pages/reports/reports.component').then(
                c => c.ReportsComponent,
              ),
          },
          {
            path: 'new',
            loadComponent: () =>
              import('./components/layout/pages/reports/new-report/new-report.component').then(
                c => c.NewReportComponent,
              ),
            canActivate: [rolesGuard],
            data: {allowedRoles: [UserRole.Accountant]},
          },
          {
            path: ':reportId',
            loadComponent: () =>
              import('./components/layout/pages/reports/view-report/view-report.component').then(
                c => c.ViewReportComponent,
              ),
          },
        ],
      },
      {
        path: RoutePathNames.SalesInvoices,
        canActivate: [uiFeatureGuard],
        canActivateChild: [uiFeatureChildGuard],
        data: {requiredFeature: CompanyUserFeature.SALES_INVOICES},
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./components/layout/pages/sales-invoices/sales-invoices.component').then(
                c => c.SalesInvoicesComponent,
              ),
          },
          {
            path: 'new',
            loadComponent: () =>
              import(
                './components/layout/pages/sales-invoices/new-invoice/new-invoice.component'
              ).then(c => c.NewInvoiceComponent),
            canActivate: [rolesGuard],
            data: {allowedRoles: [UserRole.Customer]},
          },
          {
            path: ':invoiceId',
            loadComponent: () =>
              import(
                './components/layout/pages/sales-invoices/view-invoice/view-invoice.component'
              ).then(c => c.ViewInvoiceComponent),
          },
          {
            path: ':invoiceId/edit',
            loadComponent: () =>
              import(
                './components/layout/pages/sales-invoices/edit-sales-invoice/edit-sales-invoice.component'
              ).then(c => c.EditSalesInvoiceComponent),
            canActivate: [rolesGuard],
            data: {allowedRoles: [UserRole.Accountant]},
          },
        ],
      },
      {
        path: RoutePathNames.PurchaseInvoices,
        canActivate: [isUsingReceiptsGuard, uiFeatureGuard],
        canActivateChild: [isUsingReceiptsGuard, uiFeatureChildGuard],
        data: {requiredFeature: CompanyUserFeature.PURCHASE_INVOICES},
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './components/layout/pages/purchase-invoices/purchase-invoices.component'
              ).then(c => c.PurchaseInvoicesComponent),
          },
          {
            path: ':netvisorKey',
            loadComponent: () =>
              import(
                './components/layout/pages/purchase-invoices/view-purchase-invoice/view-purchase-invoice.component'
              ).then(c => c.ViewPurchaseInvoiceComponent),
          },
        ],
      },
      {
        path: RoutePathNames.Receipts,
        canActivate: [isUsingReceiptsGuard, uiFeatureGuard],
        canActivateChild: [isUsingReceiptsGuard, uiFeatureChildGuard],
        data: {requiredFeature: CompanyUserFeature.RECEIPTS},
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./components/layout/pages/receipts/receipts.component').then(
                c => c.ReceiptsComponent,
              ),
          },
          {
            path: ':receiptId',
            loadComponent: () =>
              import('./components/layout/pages/receipts/view-receipt/view-receipt.component').then(
                c => c.ViewReceiptComponent,
              ),
          },
          {
            path: ':receiptId/edit',
            loadComponent: () =>
              import('./components/layout/pages/receipts/edit-receipt/edit-receipt.component').then(
                c => c.EditReceiptComponent,
              ),
          },
        ],
      },
      {
        path: RoutePathNames.StatementAndMaterial,
        canActivate: [isUsingReceiptsGuard, uiFeatureGuard],
        canActivateChild: [isUsingReceiptsGuard, uiFeatureChildGuard],
        data: {requiredFeature: CompanyUserFeature.RECEIPTS},
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './components/layout/pages/statement-and-material/statement-and-material.component'
              ).then(c => c.StatementAndMaterialComponent),
          },
          {
            path: ':receiptId',
            loadComponent: () =>
              import('./components/layout/pages/receipts/view-receipt/view-receipt.component').then(
                c => c.ViewReceiptComponent,
              ),
          },
          {
            path: ':receiptId/edit',
            loadComponent: () =>
              import('./components/layout/pages/receipts/edit-receipt/edit-receipt.component').then(
                c => c.EditReceiptComponent,
              ),
          },
        ],
      },
      {
        path: RoutePathNames.AccountingMaterial,
        canActivate: [isUsingAccountingMaterialGuard],
        canActivateChild: [isUsingAccountingMaterialGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './components/layout/pages/accounting-material/accounting-material.component'
              ).then(c => c.AccountingMaterialComponent),
          },
          {
            path: ':accountingMaterialId',
            loadComponent: () =>
              import(
                './components/layout/pages/accounting-material/accountant-accounting-material/view-accounting-material/view-accounting-material.component'
              ).then(c => c.ViewAccountingMaterialComponent),
          },
        ],
      },
      {
        path: RoutePathNames.Chats,
        canActivate: [uiFeatureGuard],
        data: {requiredFeature: CompanyUserFeature.CHATS},
        loadComponent: () =>
          import('./components/layout/pages/chats/chats.component').then(c => c.ChatsComponent),
      },
      {
        path: RoutePathNames.Settings,
        loadComponent: () =>
          import('./components/layout/pages/settings/settings.component').then(
            c => c.SettingsComponent,
          ),
      },
    ],
  },
  {
    path: RoutePathNames.TwoFactor,
    loadComponent: () =>
      import('../../../patja-ui/src/lib/components/two-factor-auth/two-factor-auth.component').then(
        c => c.TwoFactorAuthComponent,
      ),
    children: [
      {
        path: '',
        redirectTo: RoutePathNames.EnableTwoFactor,
        pathMatch: 'full',
      },
      {
        path: RoutePathNames.EnableTwoFactor,
        loadComponent: () =>
          import(
            '../../../patja-ui/src/lib/components/two-factor-auth/enable-two-factor/enable-two-factor.component'
          ).then(c => c.EnableTwoFactorComponent),
        canActivate: [loggedInGuard],
      },
      {
        path: RoutePathNames.ChooseMethod,
        loadComponent: () =>
          import(
            '../../../patja-ui/src/lib/components/two-factor-auth/choose-auth-method/choose-auth-method.component'
          ).then(c => c.ChooseAuthMethodComponent),
        canActivate: [loggedInGuard],
      },
      {
        path: RoutePathNames.GoogleAuthenticator,
        loadComponent: () =>
          import(
            '../../../patja-ui/src/lib/components/two-factor-auth/google-auth/google-auth.component'
          ).then(c => c.GoogleAuthComponent),
        canActivate: [loggedInGuard],
      },
      {
        path: RoutePathNames.VerifyGoogleAuthenticator,
        loadComponent: () =>
          import(
            '../../../patja-ui/src/lib/components/two-factor-auth/verify-google-auth/verify-google-auth.component'
          ).then(c => c.VerifyGoogleAuthComponent),
      },
      {
        path: RoutePathNames.BackupCodes,
        loadComponent: () =>
          import(
            '../../../patja-ui/src/lib/components/two-factor-auth/backup-codes/backup-codes.component'
          ).then(c => c.BackupCodesComponent),
        canActivate: [loggedInGuard],
      },
      {
        path: RoutePathNames.TwoFactorOptions,
        loadComponent: () =>
          import(
            '../../../patja-ui/src/lib/components/two-factor-auth/two-factor-options/two-factor-options.component'
          ).then(c => c.TwoFactorOptionsComponent),
        canActivate: [ChallengeGuard],
      },
      {
        path: RoutePathNames.VerifyBackupCode,
        loadComponent: () =>
          import(
            '../../../patja-ui/src/lib/components/two-factor-auth/two-factor-options/verify-backup-code/verify-backup-code.component'
          ).then(c => c.VerifyBackupCodeComponent),
        canActivate: [ChallengeGuard],
      },
    ],
  },
  {
    path: RoutePathNames.TermsOfUse,
    loadComponent: () =>
      import('./components/terms-of-use/terms-of-use.component').then(c => c.TermsOfUseComponent),
  },
  {
    path: RoutePathNames.PrivacyPolicy,
    loadComponent: () =>
      import('./components/privacy-policy/privacy-policy.component').then(
        c => c.PrivacyPolicyComponent,
      ),
  },
  {
    path: RoutePathNames.ReceiptSelectionResult,
    loadComponent: () =>
      import('./components/receipt-selection-result/receipt-selection-result.component').then(
        c => c.ReceiptSelectionResultComponent,
      ),
  },
  {
    path: '**',
    redirectTo: RoutePathNames.Home,
    pathMatch: 'full',
  },
];
